import { useEffect, useState } from "react";
import * as yup from "yup";

import { validations, FormValidations } from "../utils/form-validations";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FetchResponse<T = any> {
  data: T;
  statusCode: number;
  error?: string;
}

export interface FormValues {
  [k: string]: string | boolean | number | null;
}

export interface FormErrorMessages {
  [k: string]: string;
}

export interface Field {
  id: string;
  label?: string | React.ReactNode;
  initialValue?: FormValues[string];
  placeholder?: string;
  type?: string;
  maxLength?: number;
  validation?: string | yup.AnySchema;
  disabled?: boolean;
  loading?: boolean;
  options?: {
    label: string;
    value: string | number;
  }[];
  withSearch?: boolean;
  className?: string;
  radioGroupClassName?: string;
}

export type OnSubmitSuccess = (args: {
  data: FetchResponse["data"];
  formValues: FormValues;
}) => void | Promise<void>;

export interface UseFormArgs {
  fields: Field[];
  keepLoadingOnSuccess?: boolean;
  loadingValues?: boolean;
  resetOnSubmitSuccess?: boolean;
  onValidate?: (values: FormValues) => boolean;
  onSubmit?: (values: FormValues) => unknown | Promise<unknown>;
  onSubmitSuccess?: OnSubmitSuccess;
  onSubmitError?: (message: string, properties: { property: string; errorMessages: string[] }[]) => void;
  onValidationError?: (properties: { property: string; errorMessages: string[] }[]) => void;
}

export interface UseForm {
  fields: (Field & {
    value: Required<FormValues[string]>;
    errorMessage: string;
    onInput: (value: string, field: string, type?: string) => Promise<void> | void;
  })[];
  handleSubmit: () => Promise<void | FetchResponse | undefined>;
  loading: boolean;
}

const getInitialErrorMessages = (fields: Field[]) =>
  fields.reduce(
    (errorObj, field) => ({
      ...errorObj,
      [field.id]: "",
    }),
    {} as FormErrorMessages,
  );

const getInitialValues = (fields: UseFormArgs["fields"]) =>
  fields.reduce(
    (valuesObj, field) => ({
      ...valuesObj,
      [field.id]: field.initialValue ?? (field.type === "checkbox" ? false : ""),
    }),
    {} as FormValues,
  );

export const useForm = ({
  fields,
  keepLoadingOnSuccess = false,
  loadingValues,
  resetOnSubmitSuccess,
  onValidate,
  onSubmit,
  onSubmitSuccess,
  onSubmitError,
  onValidationError,
}: UseFormArgs): UseForm => {
  const [values, setValues] = useState<FormValues>(getInitialValues(fields));

  const [errorMessages, setErrorMessages] = useState<FormErrorMessages>(getInitialErrorMessages(fields));

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!loadingValues && Object.values(values).every((value) => !value)) {
      setValues(getInitialValues(fields));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingValues]);

  const handleSubmit = () => {
    const yupSchema = yup.object(
      fields.reduce((data, field) => {
        if (field.validation && typeof field.validation !== "string") {
          return {
            ...data,
            [field.id]: field.validation,
          };
        }

        const path = (field.validation || field.id) as FormValidations;

        if (!(path in validations)) {
          throw new Error(`Invalid form field: ${path}`);
        }

        return {
          ...data,
          [field.id]: validations[path],
        };
      }, {}),
    );

    const isCustomValid = onValidate ? onValidate(values) : true;

    // We may want the form to keep its loading status after a successful submit when we have to
    // to execute an action that we are unable to wait for to be completed such as router.push
    let finalLoadingStatus = !!onSubmit && isCustomValid && keepLoadingOnSuccess;

    return yupSchema
      .validate(values, { abortEarly: false })
      .then(async () => {
        setErrorMessages(getInitialErrorMessages(fields));

        if (!isCustomValid) {
          onValidationError?.([]);
          return;
        }

        if (onSubmit) {
          setLoading(true);

          try {
            const res = (await onSubmit(values)) as FetchResponse;

            if (res?.error) {
              if (onSubmitError) {
                throw res.error;
              }

              return;
            }

            if (onSubmitSuccess) {
              await onSubmitSuccess({
                data: res?.data || res,
                formValues: values,
              });
            }

            if (resetOnSubmitSuccess) {
              setValues(getInitialValues(fields));
            }

            return res;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            if (onSubmitError) {
              onSubmitError(
                typeof error === "string"
                  ? error
                  : typeof error?.message === "string"
                    ? error.message
                    : "Um erro aconteceu. Tente novamente em alguns instantes",
                error?.properties,
              );
            }

            if (error?.properties) {
              const properties: {
                property: string;
                errorMessages: string[];
              }[] = error.properties;

              setErrorMessages({
                ...errorMessages,
                ...properties.reduce((errors: FormErrorMessages, property) => {
                  return {
                    ...errors,
                    [property.property]: property.errorMessages[0],
                  };
                }, {}),
              });

              const fieldsNotFound = properties.filter(
                ({ property }) => !fields.map(({ id }) => id).includes(property),
              );

              if (fieldsNotFound.length) {
                console.error("Invalid fields:", fieldsNotFound);
              }
            }

            finalLoadingStatus = false;
          }
        }
      })
      .catch((errors) => {
        onValidationError?.(
          errors.inner.map((error: { path: string; message: string }) => ({
            property: error.path,
            errorMessages: [error.message],
          })),
        );

        setErrorMessages({
          ...getInitialErrorMessages(fields),
          ...errors.inner.reduce(
            (errorObj: FormValues, error: { path: string; message: string }) => ({
              ...errorObj,
              [error.path]: error.message,
            }),
            {},
          ),
        });

        finalLoadingStatus = false;
      })
      .finally(() => {
        setLoading(finalLoadingStatus);
      });
  };

  const onInput = async (value: string, field: string, type?: string) => {
    setValues({
      ...values,
      [field]: type === "checkbox" ? !values[field] : value,
    });
  };

  return {
    fields: fields.map((field) => ({
      ...field,
      value: values[field.id],
      errorMessage: errorMessages[field.id],
      onInput,
    })),
    handleSubmit,
    loading,
  };
};
